import { computed, nextTick, ref, type InjectionKey } from "vue";
import type { ResultOf } from "@graphql-typed-document-node/core";
import { graphql as gql } from "@/generated";
import {
  ContestsForChannelDocument,
  ContestStatus,
  type ChannelSection,
  type Show,
  ChannelSectionName,
  ShowStatus,
  WebAppIntegrationMode,
} from "@/generated/graphql";
import { useInfiniteQuery } from "@/composables/use-infinite-query";
import { useChannelCapabilities } from "@/composables/use-channel-capabilities";
import { useEmbedAuthenticationBroker } from "@/composables/auth/use-embed-authentication-broker";
import { useChannelContestLiveUpdates } from "@/features/channel/services/use-channel-contest-live-updates";
import { useChannelStore } from "@/stores/channel.store";
import { getDefaultDomainChannel } from "@/utils/channel";
import { useRoute, useRouter } from "vue-router";
import { RouteNames } from "@/router/app.router";
import { useHead } from "@unhead/vue";
import { useReferralService } from "@/features/channel/services/use-referral-service";
import { useWaitForSelector } from "@/composables/use-wait-for-selector";
import { getUrlOrAbsoluteUrlFromOrigin } from "@/utils/misc";
import { useUserStore } from "@/stores/user.store";
import { useResponsiveHelpers } from "@/composables/global/use-responsive-helpers";
import { useAnnouncementsModal } from "@/composables/channel/use-announcements-modal";
import { useI18n } from "vue-i18n";
import { useAppStore } from "@/stores/app.store";
import { useQuery } from "@urql/vue";
import { useFeatureFlags } from "@/composables/use-feature-flags";
import {
  ChannelEventType,
  useChannelEvents,
} from "@/composables/channel-events";
import { isRaffleVisible } from "@/components/channel/contests/raffle-visibility";
import type { OfferCardData } from "@/components/channel/offers/offer-card.vue";
import type { OfferDetailModalData } from "@/components/channel/offers/offer-detail-modal.vue";

/**
 * Fetches the single most-recent thread for the announcement card in the new
 * channel layout. A dedicated limit-1 query is used so the card has its own
 * URQL cache entry and does not duplicate the ThreadsSection's paginated list
 * (which runs its own full query inside <ChannelAnnouncementsModal>). Only the
 * `content` field is needed to split into title / description for the card.
 */
const LatestThreadForAnnouncementCardDocument = gql(/* GraphQL */ `
  query LatestThreadForAnnouncementCard(
    $channel_id: String!
    $community_id: String
  ) {
    threadsForChannel(
      channel_id: $channel_id
      community_id: $community_id
      page: 0
      limit: 4
    ) {
      id
      content
    }
  }
`);

const ShowsForShowSectionDocument = gql(/* GraphQL */ `
  query ShowsForShowSection(
    $channel_id: String!
    $community_id: String
    $page: Float
    $limit: Float
  ) {
    showsForShowSection(
      channel_id: $channel_id
      community_id: $community_id
      page: $page
      limit: $limit
    ) {
      title
      description
      id
      scheduled_start_time
      status

      thumbnail

      channel_id
      channel {
        name
        id
        logo
      }
      created_by {
        id
        username
        avatar
      }
      show_translations {
        language
        title
        description
      }
      show_communities {
        community {
          id
        }
      }
    }
  }
`);

const ChannelOffersForSectionDocument = gql(/* GraphQL */ `
  query ChannelOffersForSection(
    $active_only: Boolean
    $page: Float
    $limit: Float
  ) {
    channelOffers(active_only: $active_only, page: $page, limit: $limit) {
      id
      ...OfferCardData
      ...OfferDetailModalData
    }
  }
`);

const ChannelSectionsForChannelDocument = gql(/* GraphQL */ `
  query ChannelSections($channelId: String!) {
    channelSections(channel_id: $channelId) {
      id
      channel_id
      section_name
      is_visible
    }
  }
`);

/**
 * All data, derived state and handlers for the channel page. This runs inside
 * channel.vue's setup, so the lifecycle-bound composables (useHead,
 * useWaitForSelector, useEmbedAuthenticationBroker) are set up here too. The
 * result is provided under `ChannelPageKey` and consumed by the legacy / v2 body
 * components via inject.
 */
export function useChannelPage() {
  const route = useRoute();
  const { desktop } = useResponsiveHelpers();
  const userStore = useUserStore();
  const channelStore = useChannelStore();
  const referralService = useReferralService();
  const { t } = useI18n();
  const { canUseTokenGating } = useChannelCapabilities();
  const appStore = useAppStore();
  const { getBoolean } = useFeatureFlags();
  const enableCouponOffers = getBoolean("enable_coupon_offers");
  // Flag-gated swap between the legacy section order (with the
  // `channelPositionI`/grid-row token-gating hack) and the new single-column
  // order (profile card -> MEANWHILE -> benefits bar -> QUESTS -> Leaderboard ->
  // REWARDS -> Shows/Threads). Whole-page gate; legacy path untouched when off.
  const newLayout = getBoolean("enable_new_channel_layout");
  useEmbedAuthenticationBroker();

  const { emitChannelEvent } = useChannelEvents();

  // Wire the profile card's single "Upgrade / Top up" hero button (new layout
  // only) to the existing upgrade machinery hosted by the channel auth
  // orchestrator (mounted in channel-provider.vue). The card emits a single
  // CHANNEL-WIDE upgrade intent — it does NOT decide membership-vs-token,
  // because which paths exist depends on channel CAPABILITIES, which the
  // orchestrator owns.
  //
  // We emit SHOW_UPGRADE_MODAL with NO `gatingStrategyType` and NO
  // `requestedAuthorizationRule` (the channel object has no channel-level
  // `authorization_rule_group` in the GraphQL schema — it lives on experiences
  // such as Quiz/Raffle/Offer). The orchestrator's handler then resolves the
  // channel-capability-available paths and routes accordingly: 0 → no-op, single
  // → direct (membership → proration-preserving showUpgradeFlow(); token →
  // 1-token showDefaultTokenTopUp()), multiple → the upgrade-path chooser.
  function onProfileCardUpgrade() {
    emitChannelEvent({
      type: ChannelEventType.SHOW_UPGRADE_MODAL,
    });
  }

  // see-benefits: open the existing membership benefits view (GetSuperfanModal),
  // reached via the SHOW_SUPERFAN_MODAL event the orchestrator already handles.
  // Used by the profile card's "See Benefits" button.
  function onShowBenefits() {
    emitChannelEvent({
      type: ChannelEventType.SHOW_SUPERFAN_MODAL,
    });
  }

  // In the new layout the HOW-IT-WORKS card (<ChannelBenefitsBar>) is the
  // trigger for the ANNOUNCEMENTS modal — clicking it opens the modal (which
  // hosts the threads list), matching the design's "How it works" → Announcements
  // overlay. The open-state is the shared `useAnnouncementsModal` singleton so
  // the top-bar ANNOUNCEMENTS tab (in the shell, outside this tree) opens the
  // SAME modal. The profile card's "See Benefits" keeps its separate
  // SHOW_SUPERFAN_MODAL wiring (onShowBenefits) — that is a different action.
  const { isOpen: announcementsModalOpen, open: openAnnouncementsModal } =
    useAnnouncementsModal();

  function onAnnouncementsTrigger() {
    openAnnouncementsModal();
  }

  const { data: channelSections } = useQuery({
    query: ChannelSectionsForChannelDocument,
    variables: { channelId: channelStore.channelSlug! },
  });

  // Fetch the first few threads (up to 4) to power the announcement card in the
  // new layout. The query runs only when the new layout is active and a channel
  // slug is present — avoids the extra network round-trip for the legacy path.
  // The card shows the content of threads[0] split by the first newline: the
  // first line becomes the title and the remainder the description.
  const { data: latestThreadsData } = useQuery({
    query: LatestThreadForAnnouncementCardDocument,
    variables: computed(() => ({
      channel_id: channelStore.channelSlug!,
      community_id: channelStore.selectedCommunity ?? undefined,
    })),
    pause: computed(() => !channelStore.channelSlug || !newLayout.value),
  });

  /**
   * The first (most-recent) thread from the channel, used to populate the
   * announcement card title / description. Undefined when no threads exist or
   * the query has not yet resolved.
   */
  const latestThreads = computed(
    () => latestThreadsData.value?.threadsForChannel ?? [],
  );

  /**
   * Title for the announcement card: the first non-empty line of the latest
   * thread's content. Returns undefined when no thread is available (the card
   * is hidden in that case via v-if on `latestThreadTitle !== undefined`).
   */
  const latestThreadTitle = computed<string | undefined>(() => {
    const first = latestThreads.value[0];
    if (!first?.content) return undefined;
    const lines = first.content.split("\n");
    return lines[0]?.trim() || undefined;
  });

  /**
   * Description for the announcement card: the remainder of the latest thread's
   * content after the first line (trimmed). Empty string when the thread content
   * is a single line.
   */
  const latestThreadDescription = computed<string | undefined>(() => {
    const first = latestThreads.value[0];
    if (!first?.content) return undefined;
    const newlineIdx = first.content.indexOf("\n");
    if (newlineIdx === -1) return undefined;
    return first.content.slice(newlineIdx + 1).trim() || undefined;
  });

  /**
   * Number of carousel dots shown on the announcement card: the real thread
   * count capped at 4 (the static design shows four dots).
   */
  const announcementDotCount = computed(() =>
    Math.min(latestThreads.value.length, 4),
  );

  const DEFAULT_SECTIONS = [
    ChannelSectionName.CommunityFeed,
    ChannelSectionName.Leaderboard,
    ChannelSectionName.Offers,
  ];

  const initialSectionsCount = computed(() => {
    const sections =
      channelSections.value?.channelSections ??
      channelStore.channelSections ??
      [];

    if (!sections.length) {
      return 0;
    }

    const visibleInitialSections = sections.filter(
      (section) =>
        (section.section_name === ChannelSectionName.Offers &&
          enableCouponOffers.value &&
          section.is_visible) ||
        (DEFAULT_SECTIONS.includes(section.section_name) && section.is_visible),
    ).length;

    return visibleInitialSections;
  });

  const channelPositionI = computed(() => {
    return Math.max(1, initialSectionsCount.value);
  });

  const showMemberList = ref(false);

  const domain = window.location.host;
  const defaultAppChannel = getDefaultDomainChannel(domain);
  const pageTitle = computed(() =>
    channelStore.channelSlug === defaultAppChannel
      ? t("channel.titles.fanfest")
      : t("channel.titles.channelFanfest", {
          channelDescription: channelStore.channel?.description,
        }),
  );

  // Since the referral page redirects to the channel page immediately
  // after fetching the referral metadata, we set the tags here to avoid
  // any issues with OGs rendering.
  useHead({
    title: () => pageTitle.value,
    meta: [
      {
        property: "og:title",
        content: computed(
          () =>
            referralService.currentReferralData.value?.ogTitle ??
            pageTitle.value,
        ),
      },
      {
        property: "og:image",
        content: computed(() =>
          getUrlOrAbsoluteUrlFromOrigin(
            referralService.currentReferralData.value?.ogImageUrl ??
              channelStore.channel?.banner ??
              undefined,
          ),
        ),
      },
      {
        property: "og:description",
        content: computed(
          () => referralService.currentReferralData.value?.ogDescription ?? "",
        ),
      },
      {
        property: "og:type",
        content: computed(
          () =>
            referralService.currentReferralData.value?.ogType ??
            t("channel.meta.defaultOgType"),
        ),
      },
    ],
  });

  const {
    items: shows,
    loading: queryLoadingShows,
    loadMore: loadMoreShows,
    refetch: refetchShows,
    hasInitialLoad: hasInitialLoadShows,
    hasMore: hasMoreShows,
  } = useInfiniteQuery({
    query: ShowsForShowSectionDocument,
    variables: () => ({
      channel_id: channelStore.channelSlug!,
      community_id: channelStore.selectedCommunity ?? undefined,
    }),
    limit: 10,
    pause: computed(() => !channelStore.channelSlug),
    extractData: (result) => result?.showsForShowSection as Show[],
  });

  const loadingShows = computed(
    () =>
      (queryLoadingShows.value && !hasInitialLoadShows.value) ||
      !channelStore.channel,
  );

  const loadingMoreShows = computed(
    () => queryLoadingShows.value && hasInitialLoadShows.value,
  );

  const {
    items: contests,
    loading: queryLoadingContests,
    loadMore: loadMoreContests,
    refetch: refetchContests,
    hasInitialLoad: hasInitialLoadContests,
    hasMore: hasMoreContests,
  } = useInfiniteQuery({
    query: ContestsForChannelDocument,
    variables: computed(() => ({
      channel_id: channelStore.channelSlug!,
      community_id: channelStore.selectedCommunity ?? undefined,
    })),
    limit: 10,
    pause: computed(() => !channelStore.channelSlug),
    extractData: (result) =>
      result?.contestsForChannel as ResultOf<
        typeof ContestsForChannelDocument
      >["contestsForChannel"],
    // Contest pages can briefly overlap across separate refetch requests when
    // the underlying list changes between two paginated fetches — de-dup by id
    // so the keyed contest rail never receives the same contest twice.
    dedupeKey: (contest) => contest.id,
  });

  const loadingContests = computed(
    () =>
      (queryLoadingContests.value && !hasInitialLoadContests.value) ||
      !channelStore.channel,
  );

  const loadingMoreContests = computed(
    () => queryLoadingContests.value && hasInitialLoadContests.value,
  );

  // Subscribe to per-live-show quiz-list and raffle-list Ably channels so
  // the contests section reflects host actions (activate/end quiz or raffle)
  // without requiring a user-initiated page reload.
  const liveShowIds = computed(() =>
    (shows.value ?? [])
      .filter((show) => show.status === ShowStatus.Live)
      .map((show) => show.id),
  );

  useChannelContestLiveUpdates({
    channelId: computed(() => channelStore.channel?.id),
    liveShowIds,
    refetchContests,
  });

  // "X/Y Quests Completed" counter for the profile card.
  //
  // "Completed" = contest lifecycle status `ContestStatus.Ended` (per-contest,
  // NOT per-user answered/joined state). Quiz cards always stay in the
  // denominator; an ended Raffle is hidden from the QUESTS rail unless it still
  // has a winner, a prize and an end_time — `isRaffleVisible()` in
  // raffle-visibility.ts is the single source of truth for that rule (shared
  // with raffle-item-card.vue's root v-if), so the denominator matches exactly
  // what the user sees in the rail.
  //
  // Zero visible contests → "0/0". While `loadingContests` is true the label is
  // withheld (undefined) so the profile card shows its own placeholder instead
  // of a premature 0/0.
  const visibleContests = computed(() =>
    (contests.value ?? []).filter(
      (c) => c.__typename !== "Raffle" || isRaffleVisible(c),
    ),
  );

  const completedCount = computed(
    () =>
      visibleContests.value.filter((c) => c.status === ContestStatus.Ended)
        .length,
  );

  // questsCompletedLabel is the formatted counter string passed to <ChannelProfileCard>.
  // It uses an i18n key with named params so copy can be adapted per locale.
  // Format: "{completed}/{total}" (e.g. "2/5").
  const questsCompletedLabel = computed(() =>
    t("channel.profileCard.questsCompletedValue", {
      completed: completedCount.value,
      total: visibleContests.value.length,
    }),
  );

  const {
    items: offers,
    loading: queryLoadingOffers,
    loadMore: loadMoreOffers,
    refetch: refetchOffers,
    hasInitialLoad: hasInitialLoadOffers,
    hasMore: hasMoreOffers,
  } = useInfiniteQuery({
    query: ChannelOffersForSectionDocument,
    variables: () => ({ active_only: true }),
    limit: 10,
    pause: computed(() => !channelStore.channelSlug),
    extractData: (result) =>
      (result?.channelOffers ?? []) as (OfferCardData & OfferDetailModalData)[],
  });

  const loadingOffers = computed(
    () =>
      (queryLoadingOffers.value && !hasInitialLoadOffers.value) ||
      !channelStore.channel,
  );

  const loadingMoreOffers = computed(
    () => queryLoadingOffers.value && hasInitialLoadOffers.value,
  );

  useWaitForSelector(
    computed(
      () =>
        route.hash ||
        // History stack is not reactive, but since its route-dependent and we're
        // also watching the route's hash, this works
        (history.state.scrollToSelector ?? null) ||
        null,
    ),
    async (el) => {
      await nextTick();
      el.scrollIntoView({
        behavior: "smooth",
        inline: "center",
        block: "center",
      });
    },
  );

  const router = useRouter();

  /**
  When the referral code is valid (query-able), but the payload is invalid
  i.e. no experience of the type and id exists, we should redirect the user
  to the channel page.
   */
  function onInvalidReferral() {
    referralService.onInvalidReferralPayload();
    router.replace({
      name: RouteNames.Channel,
      params: { channel: channelStore.channelSlug },
    });
  }

  function clearReferralData() {
    referralService.resetAndCloseReferralModal();
  }

  function completedReferralAction() {
    refetchContests();
    clearReferralData();
  }

  function isSectionVisible(sectionName: ChannelSectionName): boolean {
    return (
      channelSections.value?.channelSections?.find(
        (section: ChannelSection) => section.section_name === sectionName,
      )?.is_visible ?? true
    );
  }

  // The button is hidden in embed mode because most SDK integrations handle
  // auth on the host side. Exception: Fanfest Auth integration mode lets
  // users log in directly inside the embedded iframe, so the button needs
  // to be visible in that case.
  const isMembershipButtonVisibleForContext = computed(
    () =>
      !appStore.isEmbed ||
      appStore.integrationMode === WebAppIntegrationMode.FanfestAuth,
  );

  // The Login / Sign-up footer is shown only to logged-out visitors and only in
  // a context where the membership CTA is allowed (mirrors the legacy
  // GetChannelMembershipButton embed guard). It renders on ALL breakpoints
  // (mobile + tablet + desktop) inside the hero panel as the grey footer
  // section. Extracted to a named computed so the panel wrapper `v-if` and the
  // footer `v-if` share one source of truth.
  const isLoginFooterVisible = computed(
    () =>
      !userStore.isAuthenticated && isMembershipButtonVisibleForContext.value,
  );

  // The `desktop.value` guard is intentionally dropped for the new-layout path
  // so the Login/Sign-up CTA shows on ALL breakpoints (mobile + tablet +
  // desktop) for unauthenticated users. The legacy path preserves the original
  // desktop-only guard so the channel-layout-legacy.vue behaviour is
  // byte-for-byte unchanged.
  //
  // Single authoritative render site for the new-layout CTA: the MEMBERSHIP CTA
  // block inside the `v-else` (new layout) branch of the channel body. The
  // channel-layout-legacy.vue mobile CTA is already dead code when the flag is
  // ON (the entire legacy body is wrapped in `v-if="!newLayout"`) — no secondary
  // guard is added there.
  const isGetMembershipButtonSkeletonVisible = computed(() => {
    const baseCondition =
      !userStore.isAuthenticated &&
      (channelStore.loadingChannel || !channelStore.channel) &&
      isMembershipButtonVisibleForContext.value;

    if (newLayout.value) {
      // New layout: all breakpoints (drop the desktop guard).
      return baseCondition;
    }
    // Legacy layout: desktop-only (original behaviour preserved).
    return desktop.value && baseCondition;
  });

  const isGetChannelMembershipButtonVisible = computed(() => {
    const baseCondition =
      !userStore.isAuthenticated && isMembershipButtonVisibleForContext.value;

    if (newLayout.value) {
      // New layout: all breakpoints (drop the desktop guard).
      return baseCondition;
    }
    // Legacy layout: desktop-only (original behaviour preserved).
    return desktop.value && baseCondition;
  });

  return {
    // Feature-flag / layout switches
    newLayout,
    enableCouponOffers,

    // Stores / services surfaced to the templates
    userStore,
    channelStore,
    referralService,

    // Section visibility + legacy grid-row token-gating hack
    isSectionVisible,
    canUseTokenGating,
    channelPositionI,

    // Contests rail
    contests,
    loadingContests,
    loadingMoreContests,
    hasMoreContests,
    refetchContests,
    loadMoreContests,

    // Shows rail
    shows,
    loadingShows,
    loadingMoreShows,
    hasMoreShows,
    loadMoreShows,
    refetchShows,

    // Offers rail
    offers,
    loadingOffers,
    loadingMoreOffers,
    hasMoreOffers,
    loadMoreOffers,
    refetchOffers,

    // Announcement card (new layout)
    latestThreadTitle,
    latestThreadDescription,
    announcementDotCount,
    onAnnouncementsTrigger,
    announcementsModalOpen,

    // Profile card (new layout)
    questsCompletedLabel,
    onProfileCardUpgrade,
    onShowBenefits,

    // Membership / login CTAs
    isLoginFooterVisible,
    isGetMembershipButtonSkeletonVisible,
    isGetChannelMembershipButtonVisible,

    // Legacy member-list modal
    showMemberList,

    // Referral modal
    onInvalidReferral,
    clearReferralData,
    completedReferralAction,
  };
}

export type ChannelPageContext = ReturnType<typeof useChannelPage>;

export const ChannelPageKey: InjectionKey<ChannelPageContext> =
  Symbol("channelPage");
